import React, { FC, ReactNode } from "react";
import Divider from "@narvar/nth-bit-divider";
import { ExtendedFee, RefundSummary } from "./types";
import { camelCase } from "./transformers/string";
import { useStyles } from "./ReviewSummaryRawStyles";
import {
  CurrencyAmount,
  ExtendedReturnItemInput,
} from "@narvar/nth-kit-returns-headless";
import { Language } from "./components";
import { Currency } from "./transformers/Currency";

interface Props {
  items?: ExtendedReturnItemInput[];
  fees?: ExtendedFee[] | null;
  refund?: RefundSummary;
  locale?: string;
  includeCurrency?: boolean;
  returnCreditsSummary?: ReactNode;
}

export const Total: FC<Props> = (props) => {
  const { items, fees, refund, locale, includeCurrency, returnCreditsSummary } =
    props;

  const styles = useStyles();

  const subtotal: CurrencyAmount =
    refund?.subtotal ??
    // fallback to sum all returning line item values
    (items ?? []).reduce(
      (ret, item) => {
        const itemPrice = item.price?.value ?? 0;
        const exchangePrice = item.exchangeTo?.price?.value ?? 0;
        ret.value += (itemPrice - exchangePrice) * item.quantity;
        return ret;
      },
      { value: 0, currency: items?.[0]?.price?.currency ?? "USD" },
    );

  const defaultLabelFee: ExtendedFee = {
    type: "LABEL_FEE",
    amount: {
      value: 0,
      currency: fees?.[0]?.amount?.currency ?? "USD",
    },
  };
  const labelFee: ExtendedFee =
    fees?.find((f) => f.type === "LABEL_FEE") ?? defaultLabelFee;

  const displayFees: ExtendedFee[] =
    refund?.displayAllFees && fees && fees.length > 0 ? fees : [labelFee];

  const total: CurrencyAmount =
    refund?.total ??
    // fallback to subtotal - fees
    displayFees.reduce(
      (ret, fee) => ({
        ...ret,
        value: ret.value - fee.amount.value,
      }),
      subtotal,
    );

  return (
    <ul className={styles.unorderedList}>
      {!refund?.hideSubtotal && (
        <li>
          <div className={styles.itemWrapper}>
            <span>
              <Language langKey="returnsReviewTotalsSubtotal" />
            </span>
            <Currency
              locale={locale}
              includeCurrency={includeCurrency}
              currency={subtotal.currency}
              value={subtotal.value}
            />
          </div>
          <Divider />
        </li>
      )}
      {displayFees.map((fee, i) => (
        <li key={i}>
          <div className={styles.itemWrapper}>
            <span>
              {fee.title ?? (
                <Language langKey="returnsReviewTotalsShippingFee" />
              )}
            </span>
            {fee.displayAmount ?? (
              <Currency
                locale={locale}
                includeCurrency={includeCurrency}
                currency={fee.amount.currency}
                value={fee.amount.value}
              />
            )}
          </div>
          <Divider />
        </li>
      ))}
      {returnCreditsSummary && (
        <>
          {returnCreditsSummary}
          <Divider />
        </>
      )}
      <li>
        <div className={styles.itemWrapper}>
          <span>
            <strong>
              <Language
                langKey={
                  total.value < 0
                    ? "returnsReviewTotalsEstimatedCharge"
                    : "returnsReviewTotalsEstimatedRefund"
                }
              />
            </strong>
          </span>
          <span>
            <strong>
              <Currency
                locale={locale}
                includeCurrency={includeCurrency}
                currency={total.currency}
                value={total.value < 0 ? total.value * -1 : total.value}
              />
            </strong>
          </span>
        </div>
        {props.refund?.refundMethod && (
          <>
            <div className={styles.itemWrapper}>
              <Language
                langKey={`returnsReviewRefundTo_${camelCase(
                  props.refund?.refundMethod?.type,
                )}`}
                replacementTokens={[
                  {
                    token: "{{name}}",
                    value: props.refund?.refundMethod?.name ?? "",
                  },
                  {
                    token: "{{description}}",
                    value: props.refund?.refundMethod?.description ?? "",
                  },
                ]}
              />
            </div>
            <div className={styles.disclaimerText}>
              <Language langKey="returnsReviewTotalsHelpText" />
            </div>
          </>
        )}
      </li>
    </ul>
  );
};
